import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import { imprimirListadoGrande } from 'lib/impresionhtml';
import {
  PROGRAMA_LISTADO_INGRESOS,
  PROGRAMA_LISTADO_OTROS_DESCUENTOS,
  puedeUsarListadoIngresos,
  puedeUsarListadoOtrosDescuentos,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx, fpDataGridSx } from 'components/financepro';

export { PROGRAMA_LISTADO_INGRESOS, PROGRAMA_LISTADO_OTROS_DESCUENTOS };

const btnBase = { textTransform: 'none', borderRadius: 1.8, fontWeight: 700, boxShadow: 'none' };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: '#FFFFFF',
  border: `1px solid ${c}`,
  '&:hover': { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
  '&.Mui-disabled': { color: FP.color.textMuted, borderColor: FP.color.border },
});
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: '#FFFFFF',
  '&:hover': { bgcolor: hover, boxShadow: 'none', color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: `${bg}66`, color: '#FFFFFF' },
});

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const fmtMoney = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v || '').replace(/,/g, ''));
  if (Number.isNaN(n)) return '0.00';
  return n.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const moneyCol = (field, headerName, width = 120) => ({
  field,
  headerName,
  width,
  align: 'right',
  headerAlign: 'right',
  valueFormatter: (value) => fmtMoney(value),
});

function NoRowsOverlay({ mensaje }) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: '100%', py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        {mensaje}
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: '#fff',
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: FP.color.navy }}>
        {value}
      </Typography>
    </Box>
  );
}

function AccesoDenegado({ title }) {
  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader title={title} subtitle="Nómina" />
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
        <Card
          elevation={0}
          sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 1.6,
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: FP.color.dangerSoft,
              color: FP.color.danger,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: FP.color.textPrimary, mb: 1 }}>
            Acceso denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem' }}>
            Su rol no tiene acceso a esta opción.
          </Typography>
        </Card>
      </Box>
    </Box>
  );
}

const CONFIG_INGRESOS = {
  title: 'Listado de Ingresos',
  subtitle: 'Ajustes, incentivos y otros ingresos extras',
  endpoint: '/api/nomina_reporteingresos',
  empty: 'Sin ingresos extras en esta nómina',
  excelName: 'ListadoIngresos',
  columns: [
    { field: 'num_emp', headerName: 'EMP', width: 80 },
    { field: 'nombres', headerName: 'NOMBRES', flex: 1, minWidth: 220 },
    moneyCol('ing_02', 'AJUSTES', 130),
    moneyCol('ing_03', 'INCENTIVOS', 130),
    moneyCol('ing_05', 'OTROS', 130),
    moneyCol('total', 'TOTAL', 130),
  ],
  excelHeaders: ['EMP', 'NOMBRES', 'AJUSTES', 'INCENTIVOS', 'OTROS', 'TOTAL'],
  excelRow: (r) => [r.num_emp, r.nombres, r.ing_02, r.ing_03, r.ing_05, r.total],
  printHeaders: ['EMP', 'NOMBRES', 'AJUSTES', 'INCENTIVOS', 'OTROS', 'TOTAL'],
  printRow: (r) => [
    r.num_emp,
    (r.nombres || '').substring(0, 35),
    fmtMoney(r.ing_02),
    fmtMoney(r.ing_03),
    fmtMoney(r.ing_05),
    fmtMoney(r.total),
  ],
  resumen: [
    { key: 'ing_02', label: 'Ajustes' },
    { key: 'ing_03', label: 'Incentivos' },
    { key: 'ing_05', label: 'Otros' },
    { key: 'total', label: 'Total' },
  ],
};

const CONFIG_DESCUENTOS = {
  title: 'Listado de Otros Descuentos',
  subtitle: 'Adicional ARS, adicional AFP y otros descuentos',
  endpoint: '/api/nomina_reporteotrosdescuentos',
  empty: 'Sin otros descuentos en esta nómina',
  excelName: 'ListadoOtrosDescuentos',
  columns: [
    { field: 'num_emp', headerName: 'EMP', width: 80 },
    { field: 'nombres', headerName: 'NOMBRES', flex: 1, minWidth: 220 },
    moneyCol('adicionalars', 'ADICIONAL ARS', 150),
    moneyCol('adicionalafp', 'ADICIONAL AFP', 150),
    moneyCol('ded_09', 'OTROS DESC', 140),
    moneyCol('total', 'TOTAL', 130),
  ],
  excelHeaders: ['EMP', 'NOMBRES', 'ADICIONAL ARS', 'ADICIONAL AFP', 'OTROS DESC', 'TOTAL'],
  excelRow: (r) => [r.num_emp, r.nombres, r.adicionalars, r.adicionalafp, r.ded_09, r.total],
  printHeaders: ['EMP', 'NOMBRES', 'ADICIONAL ARS', 'ADICIONAL AFP', 'OTROS DESC', 'TOTAL'],
  printRow: (r) => [
    r.num_emp,
    (r.nombres || '').substring(0, 35),
    fmtMoney(r.adicionalars),
    fmtMoney(r.adicionalafp),
    fmtMoney(r.ded_09),
    fmtMoney(r.total),
  ],
  resumen: [
    { key: 'adicionalars', label: 'Adicional ARS' },
    { key: 'adicionalafp', label: 'Adicional AFP' },
    { key: 'ded_09', label: 'Otros desc.' },
    { key: 'total', label: 'Total' },
  ],
};

function padNomina(numero) {
  const n = String(numero || '').trim();
  if (!n) return '';
  return n.length === 1 ? `0${n}` : n;
}

function PantallaReporteNomina({ config, puedeUsar }) {
  const { controller, selectedCompany, showError } = useProductosMaterialContext();
  const { usuarioLog, nombreNegocio } = controller;
  const { post } = useApi();

  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);

  const [ano, setAno] = useState('');
  const [numero, setNumero] = useState('');
  const [planta, setPlanta] = useState('');
  const [departamento, setDepartamento] = useState(null);
  const [departamentos, setDepartamentos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [procesado, setProcesado] = useState(false);
  const [resultado, setResultado] = useState(null);

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany],
  );

  useEffect(() => {
    if (!puedeUsar || !puedeOperar) return;
    (async () => {
      try {
        const { ok, data } = await post(config.endpoint, {
          ...credencialesEmpresa(),
          solo_catalogos: '1',
        });
        if (!ok) return;
        if (data?.periodo_activo) {
          setAno(String(data.periodo_activo.ano || ''));
          setNumero(String(data.periodo_activo.numero || ''));
        }
        if (Array.isArray(data?.departamentos)) {
          setDepartamentos(
            data.departamentos.map((d) => ({
              id: String(d.id),
              label: d.label || `${d.id}-${(d.descrip || '').trim()}`,
            })),
          );
        }
      } catch {
        /* silencioso */
      }
    })();
  }, [puedeUsar, puedeOperar, post, credencialesEmpresa, config.endpoint]);

  const procesar = async () => {
    if (!ano) {
      showError('El año no puede estar en blanco');
      return;
    }
    if (!numero) {
      showError('La nómina no puede estar en blanco');
      return;
    }
    setCargando(true);
    setProcesado(false);
    try {
      const { ok, data } = await post(config.endpoint, {
        ...credencialesEmpresa(),
        ano,
        numero,
        planta,
        departamento: departamento?.id || '',
      });
      if (!ok) throw new Error(data.Error || 'No se pudo generar el reporte');
      setResultado(data);
      setProcesado(true);
      if (!(data.empleados || []).length) {
        showError(config.empty);
      }
    } catch (e) {
      showError(e.message || 'No se pudo generar el reporte');
    } finally {
      setCargando(false);
    }
  };

  const empleados = resultado?.empleados || [];
  const rows = useMemo(
    () => empleados.map((e, idx) => ({ ...e, id: `${e.num_emp ?? 'emp'}-${idx}` })),
    [empleados],
  );

  const totales = useMemo(() => {
    const acc = {};
    config.resumen.forEach((item) => {
      acc[item.key] = empleados.reduce((suma, e) => suma + (Number(e[item.key]) || 0), 0);
    });
    return acc;
  }, [empleados, config.resumen]);

  const etiquetaNomina = `${ano || ''}-${padNomina(numero)}`;
  const empresa = resultado?.nombre_empresa || nombreNegocio || '';

  const exportarExcel = () => {
    if (!rows.length) {
      showError('No hay datos para exportar');
      return;
    }
    const hoja = XLSX.utils.aoa_to_sheet([
      config.excelHeaders,
      ...rows.map(config.excelRow),
      [],
      ['', 'TOTALES', ...config.resumen.slice(0, -1).map((item) => totales[item.key]), totales.total],
    ]);
    hoja['!cols'] = [
      { wch: 10 },
      { wch: 36 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 14 },
    ];
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, config.excelName);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
    XLSX.writeFile(libro, `${config.excelName}_${stamp}.xlsx`);
  };

  const imprimir = () => {
    if (!rows.length) {
      showError('No hay datos para imprimir');
      return;
    }
    imprimirListadoGrande({
      compania: empresa,
      titulo: config.title,
      subtitulo: `Listado ${etiquetaNomina}${planta ? ` · Planta ${planta}` : ''}${
        departamento?.label ? ` · ${departamento.label}` : ''
      }`,
      cabeceras: config.printHeaders,
      filas: rows.map(config.printRow),
      totales: ['', 'TOTALES', ...config.resumen.slice(0, -1).map((item) => fmtMoney(totales[item.key])), fmtMoney(totales.total)],
      numericas: [2, 3, 4, 5],
    });
  };

  if (!puedeUsar) {
    return <AccesoDenegado title={config.title} />;
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title={config.title}
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · ${config.subtitle}`
            : config.subtitle
        }
        cta={
          <Stack direction="row" spacing={1.2}>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!rows.length}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportarExcel}
              disabled={!rows.length}
              sx={btnOutline(FP.color.navy)}
            >
              Exportar Excel
            </Button>
          </Stack>
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Año"
                value={ano}
                onChange={(e) => setAno(e.target.value.replace(/\D/g, ''))}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Nómina"
                value={numero}
                onChange={(e) => setNumero(e.target.value.replace(/\D/g, ''))}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Planta"
                value={planta}
                onChange={(e) => setPlanta(e.target.value)}
                placeholder="(opcional)"
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Autocomplete
                options={departamentos}
                value={departamento}
                onChange={(_, v) => setDepartamento(v)}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Departamento"
                    placeholder="(opcional)"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button
              onClick={procesar}
              disabled={cargando || !puedeOperar}
              startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : null}
              sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
            >
              {cargando ? 'Procesando…' : 'Procesar'}
            </Button>
            {procesado && (
              <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                {rows.length} registros
                {resultado?.es_historica ? ' · nómina histórica' : ''}
              </Typography>
            )}
          </Box>

          {procesado && rows.length > 0 && (
            <>
              <Typography
                sx={{
                  textAlign: 'center',
                  fontWeight: 800,
                  fontSize: '1rem',
                  color: FP.color.textPrimary,
                  mb: 0.5,
                }}
              >
                {empresa}
              </Typography>
              <Typography
                sx={{
                  textAlign: 'center',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  color: FP.color.textSecondary,
                  mb: 2,
                }}
              >
                {config.title} {etiquetaNomina}
                {resultado?.periodo?.fecha1
                  ? ` · ${resultado.periodo.fecha1} al ${resultado.periodo.fecha2}`
                  : ''}
              </Typography>
              <Stack direction="row" spacing={1.2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                {config.resumen.map((item) => (
                  <ResumenChip key={item.key} label={item.label} value={fmtMoney(totales[item.key])} />
                ))}
              </Stack>
            </>
          )}

          <Box sx={{ bgcolor: '#fff' }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={config.columns}
              loading={cargando}
              rowHeight={40}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
              slots={{
                noRowsOverlay: () => <NoRowsOverlay mensaje={procesado ? config.empty : 'Procese el reporte'} />,
              }}
              sx={{ ...fpDataGridSx, '--DataGrid-overlayHeight': '220px' }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export function ListadoIngresos() {
  const { controller } = useProductosMaterialContext();
  const puedeUsar = useMemo(
    () => puedeUsarListadoIngresos(controller.roleDetalles),
    [controller.roleDetalles],
  );
  return <PantallaReporteNomina config={CONFIG_INGRESOS} puedeUsar={puedeUsar} />;
}

export function ListadoOtrosDescuentos() {
  const { controller } = useProductosMaterialContext();
  const puedeUsar = useMemo(
    () => puedeUsarListadoOtrosDescuentos(controller.roleDetalles),
    [controller.roleDetalles],
  );
  return <PantallaReporteNomina config={CONFIG_DESCUENTOS} puedeUsar={puedeUsar} />;
}

export default ListadoIngresos;
